import { useContext, useEffect, useState } from "react";
import { AppStateContext } from "../../context/AppContext";
import { AuthContext } from "../../context/AuthContext";
import useFilter from "../../hooks/useFilter";
import {
  Button,
  Input,
  Modal,
  Table,
  Row,
  Col,
  Card,
  Form,
  Checkbox,
  InputNumber,
  Select,
  TimePicker,
  Dropdown,
  Switch,
  Typography,
} from "antd";
import moment from "moment";
import {
  PlusOutlined,
  DownloadOutlined,
  MoreOutlined,
  EnvironmentOutlined,
  EditOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import { useDebouncedFunction } from "../../hooks/useDebounce";
import useRequest from "../../hooks/useRequest";
import apiPath from "../../constants/apiPath";
import { Severty, ShowToast } from "../../helper/toast";
import { useNavigate } from "react-router-dom";
import LocationMapPicker from "../../components/LocationMapPicker";
import * as XLSX from "xlsx";

function UserManagementPage() {
  const { setPageHeading } = useContext(AppStateContext);
  setPageHeading("User Management");

  return (
    <div>
      <div className="page-inner-headding">
        <UserManagement />
      </div>
    </div>
  );
}

function UserManagement() {
  const navigate = useNavigate();
  const { userProfile } = useContext(AuthContext);
  const { request } = useRequest();
  const [refresh, setRefresh] = useState(new Date().getTime());
  const [loading, setLoading] = useState(false);
  const [userList, setUserList] = useState([]);
  const [totalDocs, setTotalDocs] = useState(0);
  const [userVisible, setUserVisible] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [adminList, setAdminList] = useState([]);
  const [subAdminList, setSubAdminList] = useState([]);
  const [activityWindowHours, setActivityWindowHours] = useState(1);
  const [activityLoading, setActivityLoading] = useState(false);
  const [inactiveUsersVisible, setInactiveUsersVisible] = useState(false);
  const [activityStats, setActivityStats] = useState({
    totalUsers: 0,
    activeUsers: 0,
    inactiveUsers: 0,
    inactiveUserList: [],
  });
  const activityHourOptions = Array.from({ length: 24 }, (_, index) => {
    const value = index + 1;
    return {
      value,
      label: `Last ${value} hour${value > 1 ? "s" : ""}`,
    };
  });

  const [filters, setFilters] = useFilter({
    sortField: "created_at",
    sort: "desc",
    page: 1,
    limit: 10,
  });

  // Fetch Admins and Sub-admins separately for two filter boxes
  useEffect(() => {
    if (!userProfile) return;
    const type = userProfile.type || userProfile.userType;
    if (type === "SuperAdmin") {
      request({
        url: `${apiPath.userList}?limit=200&userType=Admin`,
        method: "GET",
        onSuccess: ({ data }) => {
          const docs = data?.list?.docs || [];
          setAdminList(docs.map((d) => ({ _id: d._id, name: d.name })));
        },
      });
      request({
        url: `${apiPath.userList}?limit=200&userType=SubAdmin`,
        method: "GET",
        onSuccess: ({ data }) => {
          const docs = data?.list?.docs || [];
          setSubAdminList(docs.map((d) => ({ _id: d._id, name: d.name })));
        },
      });
    } else if (type === "Admin") {
      setAdminList([{ _id: userProfile._id, name: "Me (Admin)" }]);
      request({
        url: `${apiPath.subAdminList}?limit=200`,
        method: "GET",
        onSuccess: ({ data }) => {
          const docs = data?.list?.docs || [];
          setSubAdminList(docs.map((d) => ({ _id: d._id, name: d.name })));
        },
      });
    } else {
      setAdminList([]);
      setSubAdminList([{ _id: userProfile._id, name: "My users" }]);
    }
  }, [userProfile?._id]);

  const getUsers = async () => {
    try {
      setLoading(true);
      const query = new URLSearchParams();
      if (filters.page) query.append("page", filters.page);
      if (filters.limit) query.append("limit", filters.limit);
      if (filters.search) query.append("search", filters.search);
      if (filters.reporterId) query.append("reporterId", filters.reporterId);
      if (filters.isActive !== undefined && filters.isActive !== "")
        query.append("isActive", filters.isActive);
      if (filters.startDate) query.append("start_date", filters.startDate);
      if (filters.endDate) query.append("end_date", filters.endDate);

      const url = `${apiPath.userManagementList}?${query.toString()}`;
      request({
        url,
        method: "GET",
        onSuccess: ({ data }) => {
          setUserList(data?.list?.docs || []);
          setTotalDocs(data?.totalDocs || 0);
          setLoading(false);
        },
        onError: (err) => {
          ShowToast(err?.message || "Failed to fetch users", Severty.ERROR);
          setLoading(false);
        },
      });
    } catch (error) {
      ShowToast(error.message, Severty.ERROR);
      setLoading(false);
    }
  };

  useEffect(() => {
    getUsers();
  }, [JSON.stringify(filters), refresh]);

  const getUserActivitySummary = () => {
    setActivityLoading(true);
    const query = new URLSearchParams();
    query.append("windowHours", String(activityWindowHours));
    if (filters.reporterId) query.append("reporterId", filters.reporterId);

    request({
      url: `${apiPath.userManagementActivitySummary}?${query.toString()}`,
      method: "GET",
      onSuccess: ({ data }) => {
        setActivityStats({
          totalUsers: data?.totalUsers || 0,
          activeUsers: data?.activeUsers || 0,
          inactiveUsers: data?.inactiveUsers || 0,
          inactiveUserList: data?.inactiveUserList || [],
        });
        setActivityLoading(false);
      },
      onError: (err) => {
        ShowToast(err?.message || "Failed to fetch activity summary", Severty.ERROR);
        setActivityLoading(false);
      },
    });
  };

  useEffect(() => {
    getUserActivitySummary();
  }, [filters.reporterId, activityWindowHours, refresh]);

  const downloadInactiveUsersExcel = () => {
    const list = activityStats.inactiveUserList || [];
    if (!list.length) {
      ShowToast("No inactive users to download", Severty.INFO);
      return;
    }
    const rows = list.map((u, i) => ({
      "#": i + 1,
      Name: u.name && u.name !== "—" ? u.name : "",
      "Mobile Number": u.mobileNumber && u.mobileNumber !== "—" ? u.mobileNumber : "",
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Inactive users");
    const filename = `inactive-users-last-${activityWindowHours}h-${new Date().toISOString().slice(0, 10)}.xlsx`;
    XLSX.writeFile(wb, filename);
    ShowToast("Excel file downloaded", Severty.SUCCESS);
  };

  const formatShift = (record) => {
    const start = record?.shiftStart;
    const end = record?.shiftEnd;
    if (!start && !end) return "—";
    return `${start || "—"} – ${end || "—"}`;
  };

  const formatLastLocation = (record) => {
    const location = record?.lastLocation;
    const isOutsideArea = record?.isOutsideArea;
    const time = record?.lastLocationAt
      ? new Date(record.lastLocationAt).toLocaleString(undefined, {
          dateStyle: "short",
          timeStyle: "short",
        })
      : "";
    if (!location && !time) return "—";
    return (
      <div style={{ maxWidth: 220 }}>
        {time ? (
          <Typography.Text
            ellipsis={{ tooltip: location }}
            style={isOutsideArea ? { color: "#ff4d4f", fontWeight: 600 } : undefined}
            title={isOutsideArea ? "User is outside the reporter's total area" : undefined}
          >
            <EnvironmentOutlined /> {time}
            {isOutsideArea ? " (Outside area)" : ""}
          </Typography.Text>
        ) : null}
        
      </div>
    );
  };

  const toggleUserStatus = (record) => {
    request({
      url: apiPath.updateUserManagementStatus,
      method: "PUT",
      data: { _id: record._id },
      onSuccess: ({ message: msg }) => {
        ShowToast(msg || "Status updated", Severty.SUCCESS);
        setRefresh(new Date().getTime());
      },
      onError: (err) => {
        ShowToast(err?.message || "Failed to update status", Severty.ERROR);
      },
    });
  };

  const openEditUser = (record) => {
    setEditingUser(record);
    setUserVisible(true);
  };

  const confirmDeleteUser = (record) => {
    Modal.confirm({
      title: "Delete user?",
      content: `Remove ${record.name || record.email}? This cannot be undone.`,
      okText: "Delete",
      okType: "danger",
      cancelText: "Cancel",
      onOk: () =>
        new Promise((resolve, reject) => {
          request({
            url: apiPath.deleteUserManagement,
            method: "DELETE",
            data: { _id: record._id },
            onSuccess: ({ message: msg }) => {
              ShowToast(msg || "User deleted", Severty.SUCCESS);
              setRefresh(new Date().getTime());
              resolve();
            },
            onError: (err) => {
              ShowToast(err?.message || "Failed to delete user", Severty.ERROR);
              reject();
            },
          });
        }),
    });
  };

  const getUserActionMenu = (record) => ({
    items: [
      {
        key: "edit",
        label: "Edit user",
        icon: <EditOutlined />,
        onClick: () => openEditUser(record),
      },
      { type: "divider" },
      {
        key: "delete",
        label: "Delete",
        danger: true,
        icon: <DeleteOutlined />,
        onClick: () => confirmDeleteUser(record),
      },
    ],
  });

  const userColumns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      width: 140,
      ellipsis: true,
      render: (name) => name || "—",
    },
    {
      title: "Mobile",
      dataIndex: "mobileNumber",
      key: "mobileNumber",
      width: 120,
      render: (mobile) => mobile || "—",
    },
    {
      title: "Shift",
      key: "shift",
      width: 130,
      render: (_, record) => formatShift(record),
    },
    {
      title: "Last location",
      key: "lastLocation",
      width: 220,
      render: (_, record) => formatLastLocation(record),
    },
    {
      title: "Location",
      key: "location",
      width: 100,
      align: "center",
      render: (_, record) => (
        <Button
          type="link"
          icon={<EnvironmentOutlined />}
          title="View on map"
          aria-label="View on map"
          style={{ fontSize: 10, padding: 0 }}
          onClick={() => navigate(`/user-management/${record._id}/location`)}
        >
          View on map
        </Button>
      ),
    },
    {
      title: "",
      key: "action",
      width: 56,
      fixed: "right",
      align: "center",
      render: (_, record) => (
        <Dropdown menu={getUserActionMenu(record)} trigger={["click"]} placement="bottomRight">
          <Button type="text" icon={<MoreOutlined />} aria-label="User actions" />
        </Dropdown>
      ),
    },
  ];

  const handleUserTableChange = ({ current, pageSize }) => {
    setFilters((prev) => ({ ...prev, page: current, limit: pageSize }));
  };

  const debouncedUserSearch = useDebouncedFunction((term) => {
    setFilters((prev) => ({ ...prev, search: term, page: 1 }));
  }, 500);

  const [exportLoading, setExportLoading] = useState(false);

  const handleExportCSV = () => {
    setExportLoading(true);
    const query = new URLSearchParams();
    if (filters.search) query.append("search", filters.search);
    if (filters.reporterId) query.append("reporterId", filters.reporterId);
    if (filters.isActive !== undefined && filters.isActive !== "")
      query.append("isActive", filters.isActive);
    if (filters.startDate) query.append("start_date", filters.startDate);
    if (filters.endDate) query.append("end_date", filters.endDate);
    const url = `${apiPath.userManagementExport}?${query.toString()}`;
    request({
      url,
      method: "GET",
      onSuccess: ({ data }) => {
        const list = data?.list || [];
        const headers = [
          "Employee ID",
          "Name",
          "Email",
          "Mobile",
          "Location",
          "Strength",
          "Start Shift",
          "End Shift",
          "Status",
          "Created At",
        ];
        const escapeCsv = (val) => {
          if (val == null) return "";
          const s = String(val);
          if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
          return s;
        };
        const rows = list.map((row) => [
          escapeCsv(row.suid),
          escapeCsv(row.name),
          escapeCsv(row.email),
          escapeCsv(row.mobileNumber),
          escapeCsv(row.location),
          escapeCsv(row.strength),
          escapeCsv(row.shiftStart),
          escapeCsv(row.shiftEnd),
          row?.isActive ? "Active" : "Inactive",
          row?.created_at
            ? new Date(row.created_at).toLocaleString("en-IN", {
                dateStyle: "short",
                timeStyle: "short",
              })
            : "",
        ]);
        const csvContent = [
          headers.join(","),
          ...rows.map((r) => r.join(",")),
        ].join("\n");
        const blob = new Blob(["\uFEFF" + csvContent], {
          type: "text/csv;charset=utf-8;",
        });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = `user-management-${new Date().toISOString().slice(0, 10)}.csv`;
        link.click();
        URL.revokeObjectURL(link.href);
        ShowToast("CSV exported successfully", Severty.SUCCESS);
        setExportLoading(false);
      },
      onError: (err) => {
        ShowToast(err?.message || "Export failed", Severty.ERROR);
        setExportLoading(false);
      },
    });
  };

  return (
    <Row gutter={[24, 0]}>
      <Col xs={24} xl={24}>
        <Card bordered={false} className="criclebox tablespace mb-24" title="User Management">
          <div className="tab_inner_tit">
            <div className="tab-upload-wrap d-flex align-items-center justify-content-between flex-wrap">
              <h3>User List (created by you)</h3>
              <div className="d-flex align-items-center gap-3 flex-wrap">
                <Select
                  style={{ width: 170 }}
                  value={activityWindowHours}
                  onChange={(v) => setActivityWindowHours(v)}
                  options={activityHourOptions}
                />
                <Button loading={activityLoading}>
                  Total Users: {activityStats.totalUsers}
                </Button>
                <Button loading={activityLoading}>
                  Active Users: {activityStats.activeUsers}
                </Button>
                <Button loading={activityLoading}>
                  Inactive Users: {activityStats.inactiveUsers}
                </Button>
                <Button
                  loading={activityLoading}
                  type="primary"
                  ghost
                  onClick={() => setInactiveUsersVisible(true)}
                >
                  List of Inactive Users
                </Button>
                <Select
                  placeholder="Admin"
                  allowClear
                  style={{ width: 180 }}
                  value={adminList.some((a) => a._id === filters.reporterId) ? filters.reporterId : undefined}
                  onChange={(v) => setFilters((p) => ({ ...p, reporterId: v || undefined, page: 1 }))}
                  options={[{ value: "", label: "All" }, ...adminList.map((a) => ({ value: a._id, label: a.name }))]}
                />
                <Select
                  placeholder="Sub-admin"
                  allowClear
                  style={{ width: 180 }}
                  value={subAdminList.some((s) => s._id === filters.reporterId) ? filters.reporterId : undefined}
                  onChange={(v) => setFilters((p) => ({ ...p, reporterId: v || undefined, page: 1 }))}
                  options={[{ value: "", label: "All" }, ...subAdminList.map((s) => ({ value: s._id, label: s.name }))]}
                />
                <Input.Search
                  placeholder="Search by name"
                  className="searchInput"
                  onSearch={(v) => setFilters((p) => ({ ...p, search: v, page: 1 }))}
                  onChange={(e) => debouncedUserSearch(e.target.value)}
                  allowClear
                  style={{ width: 220 }}
                />
                <Button type="primary" icon={<PlusOutlined />} onClick={() => setUserVisible(true)}>
                  Add User
                </Button>
              </div>
            </div>
          </div>
          <div className="table-responsive customPagination">
            <Table
              dataSource={userList}
              columns={userColumns}
              scroll={{ x: 900 }}
              pagination={{
                pageSize: filters.limit,
                current: filters.page,
                total: totalDocs || 0,
                pageSizeOptions: [10, 20, 50, 100],
                showSizeChanger: true,
              }}
              onChange={handleUserTableChange}
              rowKey={(r) => r._id}
              loading={loading}
            />
          </div>
        </Card>
      </Col>
      <UserAddModal
        visible={userVisible}
        setVisible={setUserVisible}
        refresh={() => setRefresh(new Date().getTime())}
        editingUser={editingUser}
        setEditingUser={setEditingUser}
      />
      <Modal
        open={inactiveUsersVisible}
        title={`Inactive users (last ${activityWindowHours} hour${activityWindowHours > 1 ? "s" : ""})`}
        onCancel={() => setInactiveUsersVisible(false)}
        footer={[
          <Button key="close" onClick={() => setInactiveUsersVisible(false)}>
            Close
          </Button>,
          <Button
            key="download"
            type="primary"
            icon={<DownloadOutlined />}
            onClick={downloadInactiveUsersExcel}
          >
            Download Excel
          </Button>,
        ]}
        width={560}
      >
        <Table
          dataSource={activityStats.inactiveUserList}
          rowKey={(r) => r._id}
          pagination={{ pageSize: 8, showSizeChanger: false }}
          columns={[
            {
              title: "#",
              key: "index",
              width: 60,
              render: (_, __, i) => i + 1,
            },
            {
              title: "Name",
              dataIndex: "name",
              key: "name",
              render: (v) => v || "—",
            },
            {
              title: "Mobile Number",
              dataIndex: "mobileNumber",
              key: "mobileNumber",
              render: (v) => v || "—",
            },
          ]}
          size="small"
        />
      </Modal>
    </Row>
  );
}

function UserAddModal({ visible, setVisible, refresh, editingUser, setEditingUser }) {
  const { userProfile } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [subAdminList, setSubAdminList] = useState([]);
  const [form] = Form.useForm();
  const { request } = useRequest();
  const currentType = userProfile?.type || userProfile?.userType;
  const currentId = userProfile?._id || userProfile?.id;
  const reporterSubAdminList = currentType === "SubAdmin" && currentId
    ? [{ _id: currentId, id: currentId, name: userProfile?.name, email: userProfile?.email }]
    : subAdminList;

  useEffect(() => {
    if (visible && currentType !== "SubAdmin") {
      request({
        url: `${apiPath.subAdminList}?limit=200`,
        method: "GET",
        onSuccess: ({ data }) => {
          const list = data?.list?.docs || data?.docs || [];
          setSubAdminList(list);
        },
      });
    }
    if (visible && currentType === "SubAdmin") {
      setSubAdminList([]);
    }
  }, [visible, currentType]);

  useEffect(() => {
    if (!visible) {
      form.resetFields();
      setEditingUser && setEditingUser(null);
      return;
    }
    if (editingUser) {
      form.setFieldsValue({
        reporterId: editingUser.reporterId?._id || editingUser.reporterId,
        suid: editingUser.suid ?? "",
        name: editingUser.name,
        email: editingUser.email,
        mobileNumber: editingUser.mobileNumber ?? "",
        locationData: {
          location: editingUser.location || "",
          latitude: editingUser.latitude,
          longitude: editingUser.longitude,
        },
        isActive: editingUser.isActive !== false,
        strength: editingUser.strength ?? 0,
        shiftStart: editingUser.shiftStart
          ? moment(editingUser.shiftStart, "HH:mm")
          : null,
        shiftEnd: editingUser.shiftEnd
          ? moment(editingUser.shiftEnd, "HH:mm")
          : null,
      });
    } else {
      form.resetFields();
      if (currentType === "SubAdmin" && currentId) {
        form.setFieldsValue({ reporterId: currentId });
      }
    }
  }, [visible, editingUser, currentType, currentId]);

  const handleOk = async (values) => {
    setLoading(true);
    const loc = values.locationData || {};
    const payload = {
      name: values.name,
      email: values.email,
      password: values.password,
      suid: values.suid,
      mobileNumber: values.mobileNumber || null,
      location: loc.location || null,
      latitude: loc.latitude != null ? loc.latitude : undefined,
      longitude: loc.longitude != null ? loc.longitude : undefined,
      isActive: values.isActive !== false,
      strength: values.strength ?? 0,
      reporterId: values.reporterId || undefined,
      shiftStart: values.shiftStart
        ? moment(values.shiftStart).format("HH:mm")
        : null,
      shiftEnd: values.shiftEnd
        ? moment(values.shiftEnd).format("HH:mm")
        : null,
    };

    if (editingUser?._id) {
      payload._id = editingUser._id;
      if (values.newPassword?.trim()) payload.newPassword = values.newPassword;
      request({
        url: apiPath.updateUserManagement,
        method: "PUT",
        data: payload,
        onSuccess: ({ message: msg }) => {
          ShowToast(msg, Severty.SUCCESS);
          form.resetFields();
          refresh();
          setVisible(false);
          setLoading(false);
        },
        onError: (err) => {
          ShowToast(err?.message || "Failed to update user", Severty.ERROR);
          setLoading(false);
        },
      });
    } else {
      request({
        url: apiPath.addUserManagement,
        method: "POST",
        data: payload,
        onSuccess: ({ message: msg }) => {
          ShowToast(msg, Severty.SUCCESS);
          form.resetFields();
          refresh();
          setVisible(false);
          setLoading(false);
        },
        onError: (err) => {
          ShowToast(err?.message || err?.data?.message || "Failed to add user", Severty.ERROR);
          setLoading(false);
        },
      });
    }
  };

  const hide = () => {
    setVisible(false);
    form.resetFields();
    setEditingUser && setEditingUser(null);
  };

  return (
    <Modal
      open={visible}
      title={editingUser ? "Edit User" : "Add User"}
      okText="Save"
      onCancel={hide}
      confirmLoading={loading}
      okButtonProps={{ form: "userForm", htmlType: "submit" }}
    >
      <Form id="userForm" form={form} onFinish={handleOk} layout="vertical">
        <Form.Item label="Reporter (Sub-admin)" name="reporterId" rules={[{ required: true, message: "Select reporter!" }]}>
          <Select
            placeholder="Select Sub-admin"
            allowClear={currentType !== "SubAdmin"}
            showSearch={currentType !== "SubAdmin"}
            optionFilterProp="label"
            disabled={currentType === "SubAdmin"}
            options={reporterSubAdminList.map((s) => ({ value: s._id || s.id, label: `${s.name || s.email}${s.email ? ` (${s.email})` : ""}` }))}
          />
        </Form.Item>
        <Form.Item label="Employee ID" name="suid">
          <Input placeholder="Enter Employee ID"/>
        </Form.Item>
        <Form.Item
          label="Name"
          name="name"
          rules={[{ required: true, message: "Please enter name!" }]}
        >
          <Input placeholder="Enter name" />
        </Form.Item>
        <Form.Item
          label="Email"
          name="email"
          rules={[{ type: "email", message: "Invalid email!" }]}
        >
          <Input placeholder="Enter email (optional)" disabled={!!editingUser} />
        </Form.Item>
        {!editingUser ? (
          <Form.Item
            label="Password"
            name="password"
            rules={[{ required: true, message: "Please enter password!" }]}
          >
            <Input.Password placeholder="Create password" />
          </Form.Item>
        ) : (
          <Form.Item label="New Password" name="newPassword">
            <Input.Password placeholder="Enter new password (optional)" />
          </Form.Item>
        )}
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item label="Start Shift" name="shiftStart">
              <TimePicker
                format="HH:mm"
                style={{ width: "100%" }}
                placeholder="Start shift"
                minuteStep={15}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="End Shift" name="shiftEnd">
              <TimePicker
                format="HH:mm"
                style={{ width: "100%" }}
                placeholder="End shift"
                minuteStep={15}
              />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item
          label="Mobile"
          name="mobileNumber"
          rules={[
            {
              validator: (_, value) => {
                const trimmed = value?.trim?.() ?? "";
                if (!trimmed) return Promise.resolve();
                const digits = trimmed.replace(/\D/g, "");
                if (digits.length === 10 && /^[6-9]/.test(digits)) return Promise.resolve();
                if (digits.length === 12 && digits.startsWith("91") && /^91[6-9]/.test(digits)) return Promise.resolve();
                return Promise.reject(new Error("Enter a valid 10-digit mobile number (e.g. 9876543210)"));
              },
            },
          ]}
        >
          <Input placeholder="Enter mobile number (e.g. 9876543210)" maxLength={14} />
        </Form.Item>
        <Form.Item label="Location" name="locationData" initialValue={{ location: "", latitude: undefined, longitude: undefined }}>
          <LocationMapPicker />
        </Form.Item>
        <Form.Item name="isActive" valuePropName="checked" initialValue={true}>
          <Checkbox>Active</Checkbox>
        </Form.Item>
      </Form>
    </Modal>
  );
}

export default UserManagementPage;
